import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Heading, VStack} from 'platform/foundation';

import {BranchResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {getVehicleConditionOptions} from '../utils/getVehicleConditionOptions';
import {
  getTestDriveCalendarSettingsFormSchema,
  TestDriveCalendarSettingsFormValues,
} from './getTestDriveCalendarSettingsFormSchema';
import {TestDriveCalendarBranchOverrides} from './TestDriveCalendarBranchOverrides';

interface TestDriveCalendarSettingsFormProps extends TestIdProps {
  defaultValues: Partial<TestDriveCalendarSettingsFormValues>;
  assignedBranches: BranchResponseBody[];
  onSubmit: FormSubmitHandler<TestDriveCalendarSettingsFormValues>;
  isSubmitting: boolean;
}

export function TestDriveCalendarSettingsForm(props: TestDriveCalendarSettingsFormProps) {
  const conditionOptions = getVehicleConditionOptions();
  const branchOptions = props.assignedBranches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  }));

  return (
    <Form<TestDriveCalendarSettingsFormValues>
      onSubmit={props.onSubmit}
      experimentalZodSchema={getTestDriveCalendarSettingsFormSchema()}
      defaultValues={props.defaultValues}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="multiChoice"
            isRequired
            isNotClearable
            menuInPortal
            name="branchIds"
            label={i18n.t('page.testDrive.labels.myBranches')}
            options={branchOptions}
            data-testid={suffixTestId('branchIds', props)}
          />
          <FormField
            control={control}
            type="chips"
            isMultiple
            isRequired
            name="vehicleConditions"
            label={i18n.t('page.testDrive.labels.categories')}
            options={conditionOptions}
            data-testid={suffixTestId('vehicleConditions', props)}
          />

          <Heading size={4}>{i18n.t('page.testDrive.labels.branchSettings')}</Heading>
          <TestDriveCalendarBranchOverrides
            control={control}
            assignedBranches={props.assignedBranches}
            data-testid={props['data-testid']}
          />

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.save')}
              isLoading={props.isSubmitting}
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
